import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["content", "toggle"]
  static classes = ["collapsed"]

  connect() {
    // Get initial state from localStorage or default to true (collapsed by default)
    const saved = localStorage.getItem('admin-navigation-collapsed')
    const isCollapsed = saved ? JSON.parse(saved) : true
    if (isCollapsed) {
      this.element.classList.add(this.collapsedClass)
      document.body.classList.add('navigation-collapsed')
    }
    // Label the toggle for what a click will do in the state the page loads in
    this.updateToggleLabel(isCollapsed)
    this._onSidebarMouseEnter = this.onSidebarMouseEnter.bind(this)
    this._onSidebarMouseLeave = this.onSidebarMouseLeave.bind(this)
    this._onToggleClick = this.toggle.bind(this)
    this.toggleTarget.addEventListener('click', this._onToggleClick)
    this.isSidebarHovered = false;
    this.layoutSide = this.element.closest('.layout-side')
    if (this.layoutSide) {
      this.layoutSide.addEventListener('mouseenter', this._onSidebarMouseEnter)
      this.layoutSide.addEventListener('mouseleave', this._onSidebarMouseLeave)
    }
    this.updateIcon()
  }

  disconnect() {
    this.toggleTarget.removeEventListener('click', this._onToggleClick)
    if (this.layoutSide) {
      this.layoutSide.removeEventListener('mouseenter', this._onSidebarMouseEnter)
      this.layoutSide.removeEventListener('mouseleave', this._onSidebarMouseLeave)
    }
  }

  onSidebarMouseEnter() {
    this.isSidebarHovered = true;
    if (this.element.classList.contains(this.collapsedClass)) {
      this.element.classList.add('hover-expanded')
      this.updateIcon()
    }
  }

  onSidebarMouseLeave() {
    this.isSidebarHovered = false;
    this.element.classList.remove('hover-expanded')
    this.updateIcon()
  }

  toggle() {
    if (this.element.classList.contains(this.collapsedClass)) {
      this.expand()
    } else {
      this.collapse()
    }
    this.updateIcon()
  }

  collapse() {
    this.element.classList.add(this.collapsedClass)
    this.element.classList.remove('hover-expanded')
    document.body.classList.add('navigation-collapsed')
    localStorage.setItem('admin-navigation-collapsed', 'true')
    this.updateIcon()
    this.updateToggleLabel(true)
    this.addTooltips()
    if (this.isSidebarHovered) {
      this.element.classList.add('hover-expanded')
      this.updateIcon()
    }
  }

  expand() {
    this.element.classList.remove(this.collapsedClass)
    this.element.classList.remove('hover-expanded')
    document.body.classList.remove('navigation-collapsed')
    localStorage.setItem('admin-navigation-collapsed', 'false')
    this.updateIcon()
    this.updateToggleLabel(false)
    this.removeTooltips()
  }

  updateToggleLabel(isCollapsed) {
    const label = isCollapsed ? 'Expand navigation' : 'Collapse navigation'
    this.toggleTarget.setAttribute('aria-label', label)
    this.toggleTarget.setAttribute('title', label)
  }

  addTooltips() {
    const navLinks = this.contentTarget.querySelectorAll('.nav-list-link')
    navLinks.forEach(link => {
      const label = link.querySelector('.Label')
      if (label && label.textContent.trim()) {
        link.setAttribute('title', label.textContent.trim())
      }
    })
  }

  removeTooltips() {
    const navLinks = this.contentTarget.querySelectorAll('.nav-list-link')
    navLinks.forEach(link => {
      link.removeAttribute('title')
    })
  }

  updateIcon() {
    const defaultIcon = this.toggleTarget.querySelector('.toggle-icon.default')
    const pinIcon = this.toggleTarget.querySelector('.toggle-icon.pin')
    if (this.element.classList.contains('collapsed')) {
      if (this.element.classList.contains('hover-expanded')) {
        if (defaultIcon) {
          defaultIcon.style.display = 'none';
        }
        if (pinIcon) {
          pinIcon.style.display = 'inline-block';
          pinIcon.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="16" height="16"><path d="m11.294.984 3.722 3.722a1.75 1.75 0 0 1-.504 2.826l-1.327.613a3.089 3.089 0 0 0-1.707 2.084l-.584 2.454c-.317 1.332-1.972 1.8-2.94.832L5.75 11.311 1.78 15.28a.749.749 0 1 1-1.06-1.06l3.969-3.97-2.204-2.204c-.968-.968-.5-2.623.832-2.94l2.454-.584a3.08 3.08 0 0 0 2.084-1.707l.613-1.327a1.75 1.75 0 0 1 2.826-.504ZM6.283 9.723l2.732 2.731a.25.25 0 0 0 .42-.119l.584-2.454a4.586 4.586 0 0 1 2.537-3.098l1.328-.613a.25.25 0 0 0 .072-.404l-3.722-3.722a.25.25 0 0 0-.404.072l-.613 1.328a4.584 4.584 0 0 1-3.098 2.537l-2.454.584a.25.25 0 0 0-.119.42l2.731 2.732Z"></path></svg>';
        }
      } else {
        if (defaultIcon) {
          defaultIcon.style.display = 'inline-block';
          defaultIcon.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="16" height="16"><path d="M6.823 7.823a.25.25 0 0 1 0 .354l-2.396 2.396A.25.25 0 0 1 4 10.396V5.604a.25.25 0 0 1 .427-.177Z"></path><path d="M1.75 0h12.5C15.216 0 16 .784 16 1.75v12.5A1.75 1.75 0 0 1 14.25 16H1.75A1.75 1.75 0 0 1 0 14.25V1.75C0 .784.784 0 1.75 0ZM1.5 1.75v12.5c0 .138.112.25.25.25H9.5v-13H1.75a.25.25 0 0 0-.25.25ZM11 14.5h3.25a.25.25 0 0 0 .25-.25V1.75a.25.25 0 0 0-.25-.25H11Z"></path></svg>';
        }
        if (pinIcon) {
          pinIcon.style.display = 'none';
          pinIcon.innerHTML = '';
        }
      }
    } else {
      if (defaultIcon) {
        defaultIcon.style.display = 'inline-block';
        defaultIcon.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="16" height="16"><path d="m4.177 7.823 2.396-2.396A.25.25 0 0 1 7 5.604v4.792a.25.25 0 0 1-.427.177L4.177 8.177a.25.25 0 0 1 0-.354Z"></path><path d="M0 1.75C0 .784.784 0 1.75 0h12.5C15.216 0 16 .784 16 1.75v12.5A1.75 1.75 0 0 1 14.25 16H1.75A1.75 1.75 0 0 1 0 14.25Zm1.75-.25a.25.25 0 0 0-.25.25v12.5c0 .138.112.25.25.25H9.5v-13Zm12.5 13a.25.25 0 0 0 .25-.25V1.75a.25.25 0 0 0-.25-.25H11v13Z"></path></svg>';
      }
      if (pinIcon) {
        pinIcon.style.display = 'none';
        pinIcon.innerHTML = '';
      }
    }
  }
} 
